"use client";

import React from "react";
import { CircleAlert as AlertCircle, ArrowRight, CircleDot, Flame, PhoneCall, Pipette, Search, Settings2, Sparkles, Waves, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface ServiceItem {
  id: number;
  title: string;
  description: string;
  iconName: string;
  href: string;
  badge: string;
  urgent: boolean;
}
const servicesData: ServiceItem[] = [{
  id: 1,
  title: "Emergency Plumbing",
  description: "Rapid dispatch for sudden leaks, burst pipes, and flooding hazards across Jonesboro 24 hours a day.",
  iconName: "alert",
  href: "/emergency-plumbing/",
  badge: "24/7 Priority",
  urgent: true
}, {
  id: 2,
  title: "Drain Cleaning",
  description: "Clearing heavy grease, root intrusions, and stubborn blockages in sink, shower, and floor drains.",
  iconName: "waves",
  href: "/drain-cleaning/",
  badge: "Same-Day",
  urgent: false
}, {
  id: 3,
  title: "Water Heater Repair",
  description: "Troubleshooting pilot lights, heating elements, thermostat failures, and tank leaks on all major brands.",
  iconName: "flame",
  href: "/water-heater-repair/",
  badge: "Gas & Electric",
  urgent: false
}, {
  id: 4,
  title: "Leak Detection & Repair",
  description: "Pinpointing hidden slab leaks, wall moisture, and running lines before structural damage spreads.",
  iconName: "search",
  href: "/leak-detection-repair/",
  badge: "Non-Invasive",
  urgent: false
}, {
  id: 5,
  title: "Toilet Repair",
  description: "Fixing constantly running units, hairline tank cracks, stubborn bowl clogs, and worn fill valves.",
  iconName: "wrench",
  href: "/toilet-repair/",
  badge: "All Brands",
  urgent: false
}, {
  id: 6,
  title: "Sewer Line Service",
  description: "Camera inspections, hydro-jetting, and line fixes to prevent hazardous wastewater backups into your home.",
  iconName: "pipette",
  href: "/sewer-line-repair/",
  badge: "Heavy Duty",
  urgent: false
}, {
  id: 7,
  title: "Pipe Repair & Repiping",
  description: "Replacing corroded galvanized pipes, patching punctured lines, and full whole-home PEX repiping.",
  iconName: "settings",
  href: "/pipe-repair/",
  badge: "Durable PEX/Copper",
  urgent: false
}, {
  id: 8,
  title: "General Plumbing",
  description: "Full fixture updates, faucet repairs, garbage disposals, pressure regulator checks, and home inspections.",
  iconName: "circle",
  href: "/services/",
  badge: "Maintenance",
  urgent: false
}];
export default function CoreServices() {
  const prefersReduced = useReducedMotion();
  const renderIcon = (name: string) => {
    switch (name) {
      case "alert":
        return <AlertCircle className="size-6 text-destructive" />;
      case "waves":
        return <Waves className="size-6 text-primary" />;
      case "flame":
        return <Flame className="size-6 text-accent-foreground" />;
      case "search":
        return <Search className="size-6 text-primary" />;
      case "wrench":
        return <Wrench className="size-6 text-primary" />;
      case "pipette":
        return <Pipette className="size-6 text-primary" />;
      case "settings":
        return <Settings2 className="size-6 text-primary" />;
      case "circle":
      default:
        return <CircleDot className="size-6 text-primary" />;
    }
  };
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: prefersReduced ? 0 : 0.08
      }
    }
  };
  const cardVariants = {
    hidden: prefersReduced ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="coreservices" data-nav="light" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="secondary" className="mb-3 inline-flex items-center gap-1.5 px-3 py-1 font-semibold text-secondary-foreground">
            <Sparkles className="size-3.5 text-accent-foreground" />
            <span>Complete Local Solutions</span>
          </Badge>
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Core Plumbing Services in Jonesboro
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            From sudden midnight emergencies to standard home fixture upgrades,
            our licensed technicians arrive fully equipped to resolve your
            plumbing problems fast.
          </p>
        </div>

        {/* Services Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-60px"
      }} className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {servicesData.map((service, index) => <motion.div key={service.id} data-index={index} variants={cardVariants} className="flex">
              <Card className="flex h-full w-full flex-col justify-between border-border bg-card transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md">
                <CardHeader className="space-y-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className={`flex size-12 items-center justify-center rounded-lg ${service.urgent ? "bg-destructive/10" : service.iconName === "flame" ? "bg-accent/20" : "bg-secondary"}`}>
                      {renderIcon(service.iconName)}
                    </div>
                    <Badge variant={service.urgent ? "destructive" : "secondary"} className="text-xs">
                      {service.badge}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl font-bold text-card-foreground">
                    {service.title}
                  </CardTitle>
                </CardHeader>

                <CardContent>
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {service.description}
                  </CardDescription>
                </CardContent>

                <CardFooter className="pt-2">
                  <Button asChild variant="ghost" size="sm" className="group w-full justify-between px-2 text-primary hover:bg-primary/5 hover:text-primary">
                    <Link to={service.href}>
                      <span className="font-semibold">View service details</span>
                      <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Quick Emergency CTA Bar */}
        <div className="mt-14 rounded-2xl border border-border bg-secondary/50 p-6 sm:p-8">
          <div className="flex flex-col items-center justify-between gap-6 text-center md:flex-row md:text-left">
            <div className="space-y-1">
              <h3 className="text-xl font-bold text-foreground sm:text-2xl">
                Have an active water leak or sewer backup?
              </h3>
              <p className="text-sm text-muted-foreground sm:text-base">
                Jonesboro Emergency Plumber is on standby 24/7 with rapid local
                dispatch.
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Button asChild size="lg" className="shadow-sm">
                <a href="tel:+14708479076">
                  <PhoneCall className="mr-2 size-4" />
                  Call Emergency Plumber
                </a>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/contact/">Request Service Online</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}